import { Pipe, PipeTransform } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { NotificationSubjectPipe } from "./notification-subject.pipe";

interface NotificationContentView {
    DefinitionCode: string;
    SubjectId: number;
    SubjectType?: string;
    Content: string;
    Title: string;
}

/**
 * Presents a stored notification snapshot as prose, without leaking template tokens, internal event
 * codes or the producer's internal record names.
 *
 * Snapshots are frozen at send time, so notifications already in the database keep whatever the
 * seeded template produced back then — this pipe is what makes those readable. Three shapes exist:
 *
 * 1. Real prose (newer templates) — returned unchanged.
 * 2. Only a record reference ("Wallet #12", "Đối tượng Wallet #12. Trạng thái/lý do:") — the generic
 *    seeded body with an empty reason. It says nothing the Title and the record column do not
 *    already say, so it collapses to an empty string instead of showing a half-English fragment.
 * 3. Reference plus a reason ("Wallet #12: INSUFFICIENT_FUNDS") — the reference is localized and the
 *    remainder kept.
 */
@Pipe({ name: "notificationContent", standalone: true, pure: false })
export class NotificationContentPipe implements PipeTransform {
    private static readonly UNRESOLVED_TOKEN = /\{\{?[A-Za-z_][A-Za-z0-9_.]*\}?\}/g;

    /**
     * Fixed wording of the generic seeded bodies. Removing it is what tells "reference only" apart
     * from "reference plus a real reason" — see `VLPNotificationCatalogSeeder.InAppBody/EmailBody`.
     */
    private static readonly GENERIC_SCAFFOLD = /(Đối tượng|Trạng thái\/lý do|Status\/reason)/gi;

    private readonly subject: NotificationSubjectPipe;

    constructor(private readonly transloco: TranslocoService) {
        this.subject = new NotificationSubjectPipe(transloco);
    }

    transform(item: NotificationContentView | null | undefined): string {
        if (!item) return "";

        const content = String(item.Content ?? "").trim();
        if (!content) return "";

        const hasLegacyToken = NotificationContentPipe.UNRESOLVED_TOKEN.test(content);
        NotificationContentPipe.UNRESOLVED_TOKEN.lastIndex = 0;
        const exposesDefinitionCode = !!item.DefinitionCode && content.includes(item.DefinitionCode);
        const reference = this.subjectReference(item);

        if (!hasLegacyToken && !exposesDefinitionCode && !reference) return content;

        const key = `notificationInbox.legacy-content.${item.DefinitionCode}`;
        const translated = this.transloco.translate(key, { subjectId: item.SubjectId });
        if (translated && translated !== key) return translated;

        const sanitized = this.sanitize(
            exposesDefinitionCode ? content.split(item.DefinitionCode).join("") : content,
        );
        if (!sanitized) return "";
        if (!reference) return sanitized;

        // Everything except the record reference and the generic scaffolding — empty means the
        // snapshot only ever said "which record", which the Title and record column already show.
        const remainder = this.sanitize(
            sanitized
                .replace(reference.pattern, "")
                .replace(NotificationContentPipe.GENERIC_SCAFFOLD, ""),
        );
        if (!remainder) return "";

        return sanitized.replace(reference.pattern, reference.label);
    }

    /** Localized "Ví (mã 12)" plus the pattern that matches its raw form inside the snapshot. */
    private subjectReference(
        item: NotificationContentView,
    ): { pattern: RegExp; label: string } | null {
        const rawType = String(item.SubjectType ?? "").trim();
        if (!rawType || !item.SubjectId) return null;

        const escaped = rawType.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const pattern = new RegExp(`${escaped}\\s*#\\s*${item.SubjectId}`, "gi");
        if (!pattern.test(item.Content ?? "")) return null;
        pattern.lastIndex = 0;

        return {
            pattern,
            label: this.subject.transform({ SubjectType: rawType, SubjectId: item.SubjectId }),
        };
    }

    /** Drops unresolved tokens and the punctuation they leave stranded. */
    private sanitize(value: string): string {
        const sanitized = value
            .replace(NotificationContentPipe.UNRESOLVED_TOKEN, "")
            .replace(/\s+#\s*:?(?=\s|$)/g, " ")
            .replace(/\s+([:;,.])/g, "$1")
            .replace(/\s{2,}/g, " ")
            .replace(/^\s*[-:;,./]+\s*|\s*[-:;,./]+\s*$/g, "")
            .trim();
        NotificationContentPipe.UNRESOLVED_TOKEN.lastIndex = 0;
        return sanitized;
    }
}
